import { useRef, useEffect, useMemo } from 'react';
import { useGLTF } from '@react-three/drei';
import _ from 'lodash';
import { Color } from 'three';
import * as THREE from 'three';
import { createSegmentColorMaterial } from '../../../../containers/Fiber/3dObjects/boxGeometryVertexColors';

function ObjectHighlighter({ nodes, reference, position, scale, color, rotation, polygonOffset, polygonOffsetFactor, polygonOffsetUnits, opacity = 1 }) {
  const ref = useRef();
  const meshes = nodes;

  useEffect(() => {
    ref.current.children.forEach((mesh) => {
      const cloned = mesh.material.clone();
      cloned.map = mesh.material.map;

      if (color !== undefined) {
        const tint = new Color(color);
        cloned.color.copy(tint);
        cloned.emissive.copy(tint);
        cloned.emissiveIntensity = 0.07;
      }

      const parsedOpacity = Number(opacity);
      const safeOpacity = Number.isFinite(parsedOpacity) ? Math.min(Math.max(parsedOpacity, 0), 1) : 1;
      cloned.transparent = safeOpacity < 1 || true;
      cloned.opacity = safeOpacity;
      cloned.fog = false;
      cloned.metalness = mesh.material.metalness ?? 0.5;
      cloned.roughness = mesh.material.roughness ?? 0.6;
      if (polygonOffset) {
        cloned.polygonOffset = true;
        cloned.polygonOffsetFactor = polygonOffsetFactor ?? 1;
        cloned.polygonOffsetUnits = polygonOffsetUnits ?? 1;
      }
      cloned.needsUpdate = true;
      mesh.material = cloned;
    });
  }, [color, polygonOffset, polygonOffsetFactor, polygonOffsetUnits, opacity]);

  return (
    <group ref={ref} position={position} scale={scale} rotation={rotation}>
      {meshes.map(({ uuid, geometry, material }) => (
        <mesh castShadow receiveShadow key={uuid} ref={reference} geometry={geometry} material={material} />
      ))}
    </group>
  );
}

function getColor(color) {
  if (color === undefined || color === null) return undefined;
  if (typeof color === 'number' && Number.isFinite(color)) return color;
  if (typeof color === 'string') {
    const trimmed = color.trim();
    const hex = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;
    if (!hex) return undefined;
    const parsed = parseInt(hex, 16);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
}

function desaturateColor(input, multiplier = 1) {
  try {
    const safeMul = Number.isFinite(Number(multiplier)) ? Math.max(0, Number(multiplier)) : 1;
    const c = new Color(input);
    const hsl = {};
    c.getHSL(hsl);
    c.setHSL(hsl.h, hsl.s * safeMul, hsl.l);
    return c;
  } catch {
    return new Color(input || '#808080');
  }
}

function resolveVec3(value, fallback = [0, 0, 0]) {
  if (Array.isArray(value) && value.length >= 3) {
    return [Number(value[0]) || 0, Number(value[1]) || 0, Number(value[2]) || 0];
  }
  if (value && typeof value === 'object') {
    const x = Number(value.x);
    const y = Number(value.y);
    const z = Number(value.z);
    if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
      return [x, y, z];
    }
  }
  return fallback;
}

export function WoodOutlinedColoredBeamSegmented(props) {
  const { nodes, materials } = useGLTF('/WoodOutlinedColored.glb');
  const color = getColor(_.get(props, 'color', 0));
  const colorPalette = (Array.isArray(props?.colorPalette) ? props.colorPalette : []).map((c) => (typeof c === 'string' ? c.trim() : '')).filter((c) => c !== '');
  const isPendingSolid = props?.pendingSolid === true;
  const pendingSolidColor = getColor(props?.pendingSolidColor);
  const baseColorForSolid = isPendingSolid ? pendingSolidColor ?? color : color;
  const pendingOpacity = useMemo(() => {
    const parsed = Number(props?.pendingOpacity);
    if (!Number.isFinite(parsed)) return 1;
    return Math.min(Math.max(parsed, 0), 1);
  }, [props?.pendingOpacity]);
  const saturationMultiplier = props?.desaturate ? Number(props?.saturationMultiplier ?? 0.28) : 1;
  const adjustedPalette = useMemo(() => colorPalette.map((c) => `#${desaturateColor(c, saturationMultiplier).getHexString()}`), [colorPalette.join('|'), saturationMultiplier]);
  const adjustedColor = useMemo(() => {
    if (baseColorForSolid === undefined || baseColorForSolid === null) return baseColorForSolid;
    return desaturateColor(baseColorForSolid, saturationMultiplier).getHex();
  }, [baseColorForSolid, saturationMultiplier]);

  const resolvedRotation = Array.isArray(props.rotation) && props.rotation.length === 3 ? props.rotation : [0, props.rotation || 0, 0];
  const baseScale = resolveVec3(props?.scale, [1, 1, 1]);
  const basePosition = resolveVec3(props?.position, [0, 0, 0]);

  const edgeGeometry = useMemo(() => {
    const sourceGeometry = nodes?.Cube?.geometry;
    if (!sourceGeometry) return null;
    sourceGeometry.computeBoundingBox();
    const box = sourceGeometry.boundingBox;
    if (!box) return new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1));

    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);

    const boxGeometry = new THREE.BoxGeometry(Math.max(size.x, 1e-6), Math.max(size.y, 1e-6), Math.max(size.z, 1e-6));
    boxGeometry.translate(center.x, center.y, center.z);
    const edges = new THREE.EdgesGeometry(boxGeometry);
    boxGeometry.dispose();
    return edges;
  }, [nodes?.Cube?.geometry]);

  const edgeMaterial = useMemo(
    () =>
      new THREE.LineBasicMaterial({
        color: props?.edgeColor || '#111111',
        transparent: true,
        opacity: 0.2,
        depthWrite: false
      }),
    [props?.edgeColor, props?.edgeOpacity]
  );

  const edgeScaleFactor = Number.isFinite(Number(props?.edgeScaleFactor)) ? Number(props.edgeScaleFactor) : 1;
  const edgeScale = useMemo(() => [baseScale[0] * edgeScaleFactor, baseScale[1] * edgeScaleFactor, baseScale[2] * edgeScaleFactor], [baseScale[0], baseScale[1], baseScale[2], edgeScaleFactor]);

  const paletteKey = adjustedPalette.join('|');
  const segmentMaterial = useMemo(() => {
    if (adjustedPalette.length <= 1 || !nodes?.Cube?.geometry) return null;
    const geometry = nodes.Cube.geometry;
    geometry.computeBoundingBox();
    const box = geometry.boundingBox;
    let boundaries = null;
    if (box && Number.isFinite(box.min.z) && Number.isFinite(box.max.z)) {
      const range = box.max.z - box.min.z;
      boundaries = [];
      for (let i = 1; i < adjustedPalette.length; i++) {
        boundaries.push(box.min.z + (i / adjustedPalette.length) * range);
      }
    }

    const mat = createSegmentColorMaterial(adjustedPalette, geometry, {
      axis: 'z',
      boundaries,
      transparent: pendingOpacity < 1,
      opacity: pendingOpacity
    });
    if (props.polygonOffset) {
      mat.polygonOffset = true;
      mat.polygonOffsetFactor = props.polygonOffsetFactor ?? 1;
      mat.polygonOffsetUnits = props.polygonOffsetUnits ?? 1;
    }
    mat.needsUpdate = true;
    return mat;
  }, [paletteKey, nodes?.Cube?.geometry, props.polygonOffset, props.polygonOffsetFactor, props.polygonOffsetUnits]);

  useEffect(() => {
    return () => {
      segmentMaterial?.dispose?.();
      edgeGeometry?.dispose?.();
      edgeMaterial?.dispose?.();
    };
  }, [segmentMaterial, edgeGeometry, edgeMaterial]);

  if (adjustedPalette.length > 1 && !isPendingSolid) {
    return (
      <group dispose={null}>
        <mesh
          castShadow
          receiveShadow
          key={props?.id ? props?.id : 'lamella-segmented'}
          ref={props.reference}
          geometry={nodes.Cube.geometry}
          material={segmentMaterial || materials['Material.001']}
          position={basePosition}
          scale={baseScale}
          rotation={resolvedRotation}
        />
        {edgeGeometry && <lineSegments geometry={edgeGeometry} material={edgeMaterial} position={basePosition} scale={edgeScale} rotation={resolvedRotation} renderOrder={20} />}
      </group>
    );
  }

  return (
    <group dispose={null}>
      <ObjectHighlighter
        key={props?.id ? props?.id : 'lamella'}
        name={'lamella'}
        nodes={[nodes.Cube]}
        material={materials['Material.001']}
        color={adjustedColor}
        position={basePosition}
        scale={baseScale}
        rotation={resolvedRotation}
        opacity={isPendingSolid ? pendingOpacity : 1}
        polygonOffset={props.polygonOffset}
        polygonOffsetFactor={props.polygonOffsetFactor}
        polygonOffsetUnits={props.polygonOffsetUnits}
      />
      {edgeGeometry && <lineSegments geometry={edgeGeometry} material={edgeMaterial} position={basePosition} scale={edgeScale} rotation={resolvedRotation} renderOrder={20} />}
    </group>
  );
}

useGLTF.preload('/WoodOutlinedColored.glb');
